import { useState } from 'react';
import { useLocale } from '../../context';
import { IconChevronRight } from '../VurgunIcons';
import { currencyCode } from '../../utils/currency';
import { displayUserEmail, hasUserEmail, hasUserPhone } from '../../utils/userDisplay';
import { phoneActionLabel, emailActionLabel, resolveKycDisplay } from '../account/kycDisplay';

function IconConfirmed() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="7" fill="#6b8f3d" />
      <path d="M4.5 7.1L6.2 8.8L9.7 5.3" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUnconfirmed() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="7" fill="#c9922e" />
      <path d="M7 3.6v4.2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="7" cy="10.2" r="0.85" fill="#fff" />
    </svg>
  );
}

function IconCopySmall() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.25 2.25C7.42157 2.25 6.75 2.92157 6.75 3.75V5.25H5.25C4.42157 5.25 3.75 5.92157 3.75 6.75V18.75C3.75 19.5784 4.42157 20.25 5.25 20.25H15.75C16.5784 20.25 17.25 19.5784 17.25 18.75V17.25H18.75C19.5784 17.25 20.25 16.5784 20.25 15.75V3.75C20.25 2.92157 19.5784 2.25 18.75 2.25H8.25ZM17.25 15.75H18.75V3.75H8.25V15.75H17.25ZM5.25 6.75H6.75V18.75H15.75V20.25H5.25V6.75Z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function MobileAccountProfile({ summary, onOpenPanel }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const [copied, setCopied] = useState(false);

  if (!summary) return <div className="account-loading">{tr('account.loading')}</div>;

  const kyc = resolveKycDisplay(summary, tr);
  const phonePresent = hasUserPhone(summary.phone);
  const emailPresent = hasUserEmail(summary.email);
  const phoneVerified = Boolean(summary.phone_verified);
  const emailVerified = Boolean(summary.email_verified);
  const phoneAction = phoneActionLabel(phonePresent, phoneVerified, tr);
  const emailAction = emailActionLabel(emailPresent, emailVerified, tr);

  const copyAccount = async () => {
    const value = String(summary.accountNumber || '');
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mobile-account-profile">
      <div className="mobile-account-profile__block">
        <div className="mobile-account-profile__hero">
          <span className="mobile-account-profile__name">{summary.name || '—'}</span>
          <div className="mobile-account-profile__status-row">
            <span className="mobile-account-profile__status-label">{tr('account.profile.field.verification')}</span>
            <span className={`mobile-account-profile__status-value is-${kyc.tone}`}>{kyc.text}</span>
          </div>
          {kyc.actionLabel ? (
            <button
              type="button"
              className="mobile-account-profile__kyc-btn"
              onClick={() => onOpenPanel?.('kyc')}
            >
              {kyc.actionLabel}
            </button>
          ) : null}
        </div>
      </div>

      <div className="mobile-account-profile__block">
        <h3 className="mobile-account-profile__caption">{tr('account.profile.mobile.personalData')}</h3>
        <div className="mobile-account-profile__box">
          <button type="button" className="mobile-account-profile__row" onClick={() => onOpenPanel?.('phone')}>
            <div className="mobile-account-profile__row-main">
              <div className="mobile-account-profile__row-label-row">
                <span className="mobile-account-profile__row-label">{tr('account.profile.field.phone').replace(/:$/, '')}</span>
                <span className="mobile-account-profile__link-btn" aria-hidden="true">{phoneAction}</span>
              </div>
              <span className="mobile-account-profile__row-value">
                <span>{phonePresent ? summary.phone : '—'}</span>
                {phonePresent ? (
                  <span
                    className={phoneVerified ? 'mobile-account-profile__confirmed' : 'mobile-account-profile__unconfirmed'}
                    title={phoneVerified ? tr('account.profile.status.verified') : tr('account.profile.status.unverified')}
                  >
                    {phoneVerified ? <IconConfirmed /> : <IconUnconfirmed />}
                  </span>
                ) : null}
              </span>
            </div>
            <span className="mobile-account-profile__chevron"><IconChevronRight /></span>
          </button>
          <span className="mobile-account-profile__split" aria-hidden="true" />
          <button type="button" className="mobile-account-profile__row" onClick={() => onOpenPanel?.('email')}>
            <div className="mobile-account-profile__row-main">
              <div className="mobile-account-profile__row-label-row">
                <span className="mobile-account-profile__row-label">{tr('account.profile.field.email').replace(/:$/, '')}</span>
                <span className="mobile-account-profile__link-btn" aria-hidden="true">{emailAction}</span>
              </div>
              <span className="mobile-account-profile__row-value">
                <span>{displayUserEmail(summary.email)}</span>
                {emailPresent ? (
                  <span
                    className={emailVerified ? 'mobile-account-profile__confirmed' : 'mobile-account-profile__unconfirmed'}
                    title={emailVerified ? tr('account.profile.status.verified') : tr('account.profile.status.unverified')}
                  >
                    {emailVerified ? <IconConfirmed /> : <IconUnconfirmed />}
                  </span>
                ) : null}
              </span>
            </div>
            <span className="mobile-account-profile__chevron"><IconChevronRight /></span>
          </button>
          <span className="mobile-account-profile__split" aria-hidden="true" />
          <div className="mobile-account-profile__row mobile-account-profile__row--static">
            <div className="mobile-account-profile__row-main">
              <div className="mobile-account-profile__row-label-row">
                <span className="mobile-account-profile__row-label">{tr('account.profile.mobile.passwordChange')}</span>
                <button type="button" className="mobile-account-profile__link-btn" onClick={() => onOpenPanel?.('password')}>
                  {tr('account.profile.change')}
                </button>
              </div>
              <span className="mobile-account-profile__row-value">**********</span>
            </div>
          </div>
          <span className="mobile-account-profile__split" aria-hidden="true" />
          <button type="button" className="mobile-account-profile__row" onClick={() => onOpenPanel?.('passkeys')}>
            <div className="mobile-account-profile__row-main">
              <span className="mobile-account-profile__row-label">{tr('account.profile.mobile.passkeys')}</span>
              <span className="mobile-account-profile__row-value">{tr('account.profile.mobile.passkeysHint')}</span>
            </div>
            <span className="mobile-account-profile__chevron"><IconChevronRight /></span>
          </button>
        </div>
      </div>

      <div className="mobile-account-profile__block">
        <h3 className="mobile-account-profile__caption">{tr('account.profile.mobile.aboutAccount')}</h3>
        <div className="mobile-account-profile__box">
          <div className="mobile-account-profile__account-row">
            <span className="mobile-account-profile__row-label">{tr('account.profile.field.accountNo').replace(/:$/, '')}</span>
            <span className="mobile-account-profile__account-value">
              <span>{summary.accountNumber || '—'}</span>
              <button type="button" className="mobile-account-profile__copy" onClick={copyAccount} aria-label={tr('mobile.account.copy')}>
                <IconCopySmall />
              </button>
            </span>
          </div>
          <span className="mobile-account-profile__split" aria-hidden="true" />
          <div className="mobile-account-profile__account-row">
            <span className="mobile-account-profile__row-label">{tr('account.profile.field.currency').replace(/:$/, '')}</span>
            <span className="mobile-account-profile__account-plain">{currencyCode(summary.currency)}</span>
          </div>
        </div>
      </div>

      {copied ? <div className="mobile-account-profile__copied">{tr('mobile.account.copied')}</div> : null}
    </div>
  );
}
